import * as dasha from "@dasha.ai/sdk/web";

// Add consolidated voice types at the top of the file
export type VoiceGender = 'male' | 'female';
export type VoiceLanguage = string; // e.g. 'en-US', 'ru-RU'

export interface VoiceCommon {
  providerVoiceId: string; //Provider's voice ID
  language: string;
  provider: string;
  gender?: VoiceGender;
  name?: string;
  description?: string;
  labels?: Record<string, string>;
}

// Update BaseVoiceSettings to extend VoiceCommon and remove duplicate properties
export interface BaseVoiceSettings extends VoiceCommon {
  model: string;
  speed: number;
  options: {} //Provider-specific options
}

export interface Voice extends VoiceCommon {
    id: string; //Dasha API voice ID
}

// Initial settings type that only requires language
export type InitialVoiceSettings = Pick<BaseVoiceSettings, 'language'>;

// Provider-specific voice settings
export interface ElevenLabsVoiceSettings extends BaseVoiceSettings {
  provider: typeof VOICE_PROVIDERS.ELEVEN_LABS;
  speed: number;
  options: {
    similarity_boost: number;
    stability: number;
    style: number;
    use_speaker_boost: boolean;
    optimize_streaming_latency: number;
  };
}

export interface CartesiaVoiceSettings extends BaseVoiceSettings {
  provider: typeof VOICE_PROVIDERS.CARTESIA;
  speed: number;  // -1.0 to 1.0
  options: {
    emotions: string[];  // List of emotion settings
  };
}

export interface DashaVoiceSettings extends BaseVoiceSettings {
  provider: typeof VOICE_PROVIDERS.DASHA,
  speed: number;  //0.5 to 2
}

export interface InworldVoiceSettings extends BaseVoiceSettings {
  provider: typeof VOICE_PROVIDERS.INWORLD,
  speed: number;  //0.5 to 2
  options: {
    temperature: number;
    pitch: number;
  }
}

export interface LmntVoiceSettings extends BaseVoiceSettings {
  provider: typeof VOICE_PROVIDERS.LMNT,
  options: {
    temperature?: number;
    top_p?: number;
    seed?: number;
  }
}

export interface MinimaxVoiceSettings extends BaseVoiceSettings {
  provider: typeof VOICE_PROVIDERS.MINIMAX,
  speed: number;  // 0.5 to 2
  options: {
    pitch?: number;               // -12 to 12
    text_normalization?: boolean;
    language_boost?: string;
  }
}

export interface XaiVoiceSettings extends BaseVoiceSettings {
  provider: typeof VOICE_PROVIDERS.XAI,
  speed: number;  // 0.7 to 1.5
  options: {
    optimize_streaming_latency?: number;  // 0 = best quality, 1 = lower latency
  }
}

export const CARTESIA_EMOTIONS = [
  'anger:lowest',
  'anger:low',
  'anger:high',
  'anger:highest',
  'positivity:lowest',
  'positivity:low',
  'positivity:high',
  'positivity:highest',
  'surprise:lowest',
  'surprise:low',
  'surprise:high',
  'surprise:highest',
  'sadness:lowest',
  'sadness:low',
  'sadness:high',
  'sadness:highest',
  'curiosity:lowest',
  'curiosity:low',
  'curiosity:high',
  'curiosity:highest'
] as const;

export type ProviderVoiceSettings = ElevenLabsVoiceSettings | CartesiaVoiceSettings | DashaVoiceSettings | InworldVoiceSettings | LmntVoiceSettings | MinimaxVoiceSettings | XaiVoiceSettings;

export const VOICE_PROVIDERS = {
  ELEVEN_LABS: 'ElevenLabs',
  CARTESIA: 'Cartesia',
  DASHA: 'Dasha',
  INWORLD: 'Inworld',
  LMNT: 'Lmnt',
  MINIMAX: 'Minimax',
  XAI: 'Xai'
} as const;

export const VOICE_PROVIDERS_WITH_CLONE = [
  VOICE_PROVIDERS.ELEVEN_LABS,
  VOICE_PROVIDERS.CARTESIA,
  VOICE_PROVIDERS.LMNT,
  VOICE_PROVIDERS.INWORLD,
  VOICE_PROVIDERS.MINIMAX
] as const;

export const VOICE_MODELS = {
  [VOICE_PROVIDERS.ELEVEN_LABS]: [
    { id: 'eleven_multilingual_v2', name: 'Multilingual V2' },
    { id: 'eleven_turbo_v2_5', name: 'Turbo V2.5' },
    { id: 'eleven_flash_v2_5', name: 'Flash V2.5' },
    { id: 'custom', name: 'Custom', isCustom: true }
  ],
  [VOICE_PROVIDERS.CARTESIA]: [
    { id: 'sonic-3.5', name: 'Sonic 3.5' },
    { id: 'custom', name: 'Custom', isCustom: true }
  ],
  [VOICE_PROVIDERS.DASHA]: [
    { id: 'common', name: 'Common'}
  ],
  [VOICE_PROVIDERS.INWORLD]: [
    { id: 'inworld-tts-1', name: 'inworld-tts-1' }
  ],
  [VOICE_PROVIDERS.LMNT]: [
    { id: 'blizzard', name: 'Blizzard' }
  ],
  [VOICE_PROVIDERS.MINIMAX]: [
    { id: 'speech-2.8-turbo', name: 'Speech 2.8 Turbo' },
    { id: 'speech-2.6-hd', name: 'Speech 2.6 HD' }
  ]
} as const;

export const VOICE_LANGUAGES: Record<string, LanguageOption[]> = {
  [VOICE_PROVIDERS.CARTESIA]: [
    { value: 'en-US', label: 'English (USA)', flag: 'us' },
    { value: 'fr-FR', label: 'French (France)', flag: 'fr' },
    { value: 'de-DE', label: 'German', flag: 'de' },
    { value: 'es-ES', label: 'Spanish (Spain)', flag: 'es' },
    { value: 'pt-BR', label: 'Portuguese (Brazil)', flag: 'br' },
    { value: 'zh-CN', label: 'Chinese (China)', flag: 'cn' },
    { value: 'ja-JP', label: 'Japanese (Japan)', flag: 'jp' },
    { value: 'hi-IN', label: 'Hindi (India)', flag: 'in' },
    { value: 'it-IT', label: 'Italian', flag: 'it' },
    { value: 'ko-KR', label: 'Korean (South Korea)', flag: 'kr' },
    { value: 'nl-NL', label: 'Dutch', flag: 'nl' },
    { value: 'pl-PL', label: 'Polish', flag: 'pl' },
    { value: 'ru-RU', label: 'Russian (Russia)', flag: 'ru' },
    { value: 'sv-SE', label: 'Swedish', flag: 'se' },
    { value: 'tr-TR', label: 'Turkish', flag: 'tr' },
  ],
  [VOICE_PROVIDERS.DASHA]: [
    { value: 'en-US', label: 'English (USA)', flag: 'us' },
    { value: 'ru-RU', label: 'Russian (Russia)', flag: 'ru' },
  ],

  [VOICE_PROVIDERS.LMNT]: [
    { value: 'en-US', label: 'English (USA)', flag: 'us' },
    { value: 'zh-CN', label: 'Chinese (China)', flag: 'cn' },
    { value: 'fr-FR', label: 'French (France)', flag: 'fr' },
    { value: 'de-DE', label: 'German', flag: 'de' },
    { value: 'pt-BR', label: 'Portuguese (Brazil)', flag: 'br' },
    { value: 'es-ES', label: 'Spanish (Spain)', flag: 'es' },
    { value: 'ko-KR', label: 'Korean (South Korea)', flag: 'kr' },
    { value: 'hi-IN', label: 'Hindi (India)', flag: 'in' },
    { value: 'ja-JP', label: 'Japanese (Japan)', flag: 'jp' },
    { value: 'ru-RU', label: 'Russian (Russia)', flag: 'ru' },
    { value: 'it-IT', label: 'Italian', flag: 'it' },
    { value: 'tr-TR', label: 'Turkish', flag: 'tr' },
    { value: 'nl-NL', label: 'Dutch', flag: 'nl' },
    { value: 'id-ID', label: 'Indonesian', flag: 'id' },
    { value: 'pl-PL', label: 'Polish', flag: 'pl' },
    { value: 'sv-SE', label: 'Swedish', flag: 'se' },
    { value: 'th-TH', label: 'Thai', flag: 'th' },
    { value: 'uk-UA', label: 'Ukrainian', flag: 'ua' },
    { value: 'vi-VN', label: 'Vietnamese', flag: 'vn' }
  ]
}

// Default settings for each provider
export const DEFAULT_PROVIDER_SETTINGS: Record<string, ProviderVoiceSettings> = {
  [VOICE_PROVIDERS.ELEVEN_LABS]: {
    providerVoiceId: "cgSgspJ2msm6clMCkdW9", // default voice id for ElevenLabs
    language: 'en-US',
    provider: VOICE_PROVIDERS.ELEVEN_LABS,
    model: 'eleven_flash_v2_5',
    speed: 1.0,
    options: {
      similarity_boost: 0.75,
      stability: 0.5,
      style: 0.3,
      use_speaker_boost: true,
      optimize_streaming_latency: 4
    }
  },
  [VOICE_PROVIDERS.CARTESIA]: {
    providerVoiceId: "3b78efc1-b4d1-4efb-8964-5d22ce0c43aa", // default voice id for Cartesia
    language: 'en-US',
    provider: VOICE_PROVIDERS.CARTESIA,
    model: 'sonic-3.5',
    speed: 0,
    options: {
      emotions: []
    }
  },
  [VOICE_PROVIDERS.DASHA]: {
    providerVoiceId: "jack",
    language: 'en-US',
    provider: VOICE_PROVIDERS.DASHA,
    model: 'common',
    speed: 1,
    options: {}
  },
  [VOICE_PROVIDERS.INWORLD]: {
    providerVoiceId: "inworld-tts-1",
    language: 'en-US',
    provider: VOICE_PROVIDERS.INWORLD,
    model: 'inworld-tts-1',
    speed: 1,
    options: {
      temperature: 0.8,
      pitch: 0
    }
  },
  [VOICE_PROVIDERS.LMNT]: {
    providerVoiceId: "ava",
    language: 'en-US',
    provider: VOICE_PROVIDERS.LMNT,
    model: 'blizzard',
    speed: 1,
    options: {
      temperature: 1,
      top_p: 0.8
    }
  },
  [VOICE_PROVIDERS.MINIMAX]: {
    providerVoiceId: "English_Whispering_girl",
    language: 'en-US',
    provider: VOICE_PROVIDERS.MINIMAX,
    model: 'speech-2.8-turbo',
    speed: 1,
    options: {}
  },
  [VOICE_PROVIDERS.XAI]: {
    providerVoiceId: "altair",
    language: 'en-US',
    provider: VOICE_PROVIDERS.XAI,
    model: '', // xAI has no model concept; the speaker is just Xai/<voiceId>
    speed: 1,
    options: {
      optimize_streaming_latency: 1
    }
  }
};

export const AVAILABLE_LANGUAGES: LanguageOption[] = [
  // English variants
  { value: 'en-US', label: 'English (USA)', flag: 'us' },
  { value: 'en-GB', label: 'English (UK)', flag: 'gb' },
  { value: 'en-AU', label: 'English (Australia)', flag: 'au' },
  { value: 'en-CA', label: 'English (Canada)', flag: 'ca' },

  // European languages
  { value: 'de-DE', label: 'German', flag: 'de' },
  { value: 'fr-FR', label: 'French (France)', flag: 'fr' },
  { value: 'fr-CA', label: 'French (Canada)', flag: 'ca' },
  { value: 'pt-BR', label: 'Portuguese (Brazil)', flag: 'br' },
  { value: 'pt-PT', label: 'Portuguese (Portugal)', flag: 'pt' },
  { value: 'it-IT', label: 'Italian', flag: 'it' },
  { value: 'es-ES', label: 'Spanish (Spain)', flag: 'es' },
  { value: 'es-MX', label: 'Spanish (Mexico)', flag: 'mx' },
  { value: 'nl-NL', label: 'Dutch', flag: 'nl' },
  { value: 'tr-TR', label: 'Turkish', flag: 'tr' },
  { value: 'pl-PL', label: 'Polish', flag: 'pl' },
  { value: 'sv-SE', label: 'Swedish', flag: 'se' },
  { value: 'bg-BG', label: 'Bulgarian', flag: 'bg' },
  { value: 'ro-RO', label: 'Romanian', flag: 'ro' },
  { value: 'cs-CZ', label: 'Czech', flag: 'cz' },
  { value: 'el-GR', label: 'Greek', flag: 'gr' },
  { value: 'fi-FI', label: 'Finnish', flag: 'fi' },
  { value: 'hr-HR', label: 'Croatian', flag: 'hr' },
  { value: 'sk-SK', label: 'Slovak', flag: 'sk' },
  { value: 'da-DK', label: 'Danish', flag: 'dk' },
  { value: 'uk-UA', label: 'Ukrainian', flag: 'ua' },
  { value: 'ru-RU', label: 'Russian', flag: 'ru' },
  { value: 'hu-HU', label: 'Hungarian', flag: 'hu' },
  { value: 'no-NO', label: 'Norwegian', flag: 'no' },
  
  // Asian and other languages
  { value: 'ja-JP', label: 'Japanese', flag: 'jp' },
  { value: 'zh-CN', label: 'Chinese', flag: 'cn' },
  { value: 'ko-KR', label: 'Korean', flag: 'kr' },
  { value: 'hi-IN', label: 'Hindi', flag: 'in' },
  { value: 'id-ID', label: 'Indonesian', flag: 'id' },
  { value: 'fil-PH', label: 'Filipino', flag: 'ph' },
  { value: 'ms-MY', label: 'Malay', flag: 'my' },
  { value: 'ta-IN', label: 'Tamil', flag: 'in' },
  { value: 'vi-VN', label: 'Vietnamese', flag: 'vn' },
  
  // Arabic variants
  { value: 'ar-SA', label: 'Arabic (Saudi Arabia)', flag: 'sa' },
  { value: 'ar-AE', label: 'Arabic (UAE)', flag: 'ae' }
];

// Helper function to get language name from code
export function getLanguageName(langCode: string): string {
  const language = AVAILABLE_LANGUAGES.find(lang => lang.value === langCode);
  return language?.label || langCode;
}

// Add the LanguageOption interface before its usage
// Export language mapping for reuse
export interface LanguageOption {
  value: string;
  label: string;
  flag: string;
}

// Add helper function to handle voice changes
export function createVoiceSettings(
  voice: dasha.coreapi.TtsCloneResponseDto,
  currentSettings: ProviderVoiceSettings | undefined
): ProviderVoiceSettings {
  const provider = voice.provider;
  console.log('Voice change - Provider:', provider);
  
  const baseSettings = {
    provider,
    providerVoiceId: voice.voiceId,
    language: (currentSettings?.language) || voice.language || '',
    model: DEFAULT_PROVIDER_SETTINGS[provider].model,
  };
  console.log('Voice change - Base settings:', baseSettings);

  if (provider === VOICE_PROVIDERS.INWORLD) {
    return {
      ...baseSettings,
      speed: (DEFAULT_PROVIDER_SETTINGS[VOICE_PROVIDERS.INWORLD] as InworldVoiceSettings).speed,
      options: DEFAULT_PROVIDER_SETTINGS[VOICE_PROVIDERS.INWORLD].options
    } as InworldVoiceSettings;
  }
  if (provider === VOICE_PROVIDERS.MINIMAX) {
    return {
      ...baseSettings,
      speed: (DEFAULT_PROVIDER_SETTINGS[VOICE_PROVIDERS.MINIMAX] as MinimaxVoiceSettings).speed,
      options: DEFAULT_PROVIDER_SETTINGS[VOICE_PROVIDERS.MINIMAX].options
    } as MinimaxVoiceSettings;
  }
  if (provider === VOICE_PROVIDERS.XAI) {
    return {
      ...baseSettings,
      speed: (DEFAULT_PROVIDER_SETTINGS[VOICE_PROVIDERS.XAI] as XaiVoiceSettings).speed,
      options: DEFAULT_PROVIDER_SETTINGS[VOICE_PROVIDERS.XAI].options
    } as XaiVoiceSettings;
  }
  const defaultSettings = provider === VOICE_PROVIDERS.CARTESIA
    ? {
        ...baseSettings,
        speed: (DEFAULT_PROVIDER_SETTINGS[VOICE_PROVIDERS.CARTESIA] as CartesiaVoiceSettings).speed,
        options: DEFAULT_PROVIDER_SETTINGS[VOICE_PROVIDERS.CARTESIA].options
      } as CartesiaVoiceSettings
    : {
        ...baseSettings,
        options: DEFAULT_PROVIDER_SETTINGS[VOICE_PROVIDERS.ELEVEN_LABS].options
      } as ElevenLabsVoiceSettings;
  
  console.log('Voice change - Final settings:', defaultSettings);

  return {
    ...(currentSettings as ProviderVoiceSettings),
    ...defaultSettings
  };
}

// Add type guards and utility functions
export const isElevenLabsSettings = (settings: ProviderVoiceSettings): settings is ElevenLabsVoiceSettings => {
  return settings.provider === VOICE_PROVIDERS.ELEVEN_LABS && 
         'options' in settings && 
         'similarity_boost' in settings.options;
};

export const isCartesiaSettings = (settings: ProviderVoiceSettings): settings is CartesiaVoiceSettings => {
  return settings.provider === VOICE_PROVIDERS.CARTESIA && 
         'options' in settings && 
         'emotions' in settings.options;
};

export const isLmntSettings = (settings: ProviderVoiceSettings): settings is LmntVoiceSettings => {
  return settings.provider === VOICE_PROVIDERS.LMNT;
};

export const findVoiceById = (voices: dasha.coreapi.TtsCloneResponseDto[], voiceId: string): dasha.coreapi.TtsCloneResponseDto | undefined => {
  return voices.find(v => v.voiceId === voiceId);
};

export const getSearchableVoiceContent = (voice: dasha.coreapi.TtsCloneResponseDto): string => {
  return [
    voice.name,
    voice.description,
    voice.provider.toString(),
    voice.voiceId,
    voice.language,
    voice.labels?.gender,
    ...Object.values(voice.labels || {})
  ].filter(Boolean).join(' ');
};

export interface ProviderVoiceDTO {
  speed?: number;
  speaker: string;
  lang: string;
  options: { [key: string]: any }
}

export const transformProviderVoiceSettingsToDTO = (voice: ProviderVoiceSettings): ProviderVoiceDTO => {
  const voiceDTO = {
    speed: 1,
    speaker: voice.provider + "/" + voice.model + "/" + voice.providerVoiceId,
    lang: voice.language,
    options: voice.options,
  };
  if (voice.provider === "Cartesia") {
    voiceDTO.speed = voice.speed + 1;
  }
  if (voice.provider === "Dasha") {
    voiceDTO.speed = voice.speed;
    voiceDTO.speaker = "Dasha2/" + voice.providerVoiceId;
  }
  if (voice.provider === "ElevenLabs") {
    voiceDTO.speed = voice.speed;
  }
  if (voice.provider === "Inworld") {
    voiceDTO.speed = voice.speed;
  }
  if (voice.provider === "Minimax") {
    voiceDTO.speed = voice.speed;
  }
  if (voice.provider === "Xai") {
    voiceDTO.speed = voice.speed;
    voiceDTO.speaker = "Xai/" + voice.providerVoiceId;
  }

  return voiceDTO;
};
